/**
 * Passwordless hand-off (FP-3569). After set-password creates the FusionAuth
 * user, the account service starts a passwordless login and returns its
 * one-time code. Sending the browser to `/oauth2/passwordless/{code}` makes
 * FusionAuth perform a real login — SSO session, MFA login policy — and return
 * an authorization code to our ordinary `/login` callback, instead of the
 * portal adopting the registration tokens (which no login policy ever sees).
 *
 * The URL carries exactly what a normal FusionAuth sign-in's authorize request
 * carries (see `store/auth/signIn/saga.ts`), including the PKCE pair and the
 * `state.csrf` nonce stashed in this tab, so the callback redeems the returned
 * code unchanged.
 *
 * home-portal's invite acceptance hands its code over through sessionStorage
 * (`readPasswordlessHandoff`), not a URL parameter (FP-3686). A link-triggerable
 * entry point would be a login CSRF: a code an attacker minted for their own
 * account could be planted in a link to sign someone else in as the attacker.
 * Storage is per origin and per tab, so the hand-off is bound to the tab that
 * completed activation, and the one-time code never appears in a B-Stock URL,
 * history, or access log.
 */
import queryString from 'query-string'

import { ddLogger } from '@b-stock/fe-core/bstock-react'

import appConfig from '@config/config'
import { getOrigin } from '@helpers/windowLocation'

import type { OAuthChallenge } from './oauthChallenge'
import {
  challengeParams,
  clearReauthorizeAttempted,
  isWebCryptoAvailable,
  mintOAuthChallenge,
  storeOAuthChallenge,
  withCsrf,
} from './oauthChallenge'

const { api, baseUrl } = appConfig

const {
  oauth2,
  auth: { clientId },
} = api

/** sessionStorage key home-portal writes the `/acct/login` hand-off under. */
export const PASSWORDLESS_HANDOFF_STORAGE_KEY = 'passwordless_handoff'

/** The stored hand-off: JSON `{"code": "...", "redirectAfterLogin": "..."}`. */
export type PasswordlessHandoff = { code: string; redirectAfterLogin?: string }

const nonEmptyString = (value: unknown): string | undefined =>
  typeof value === 'string' && value ? value : undefined

/**
 * Reads and consumes the hand-off home-portal left in this tab. The entry is
 * removed before it is parsed, so even a malformed one is used at most once.
 * `null` when there is none, it is malformed, or storage is unavailable.
 */
export const readPasswordlessHandoff = (): PasswordlessHandoff | null => {
  try {
    const raw = window.sessionStorage.getItem(PASSWORDLESS_HANDOFF_STORAGE_KEY)
    if (raw === null) {
      return null
    }
    window.sessionStorage.removeItem(PASSWORDLESS_HANDOFF_STORAGE_KEY)
    let parsed: unknown
    try {
      parsed = JSON.parse(raw)
    } catch {
      ddLogger.warn('Ignoring a malformed passwordless hand-off entry')
      return null
    }
    if (typeof parsed !== 'object' || parsed === null) {
      ddLogger.warn('Ignoring a malformed passwordless hand-off entry')
      return null
    }
    const entry = parsed as Record<string, unknown>
    const code = nonEmptyString(entry.code)
    if (!code) {
      ddLogger.warn('Ignoring a malformed passwordless hand-off entry')
      return null
    }
    const redirectAfterLogin = nonEmptyString(entry.redirectAfterLogin)
    return redirectAfterLogin ? { code, redirectAfterLogin } : { code }
  } catch {
    return null
  }
}

/**
 * Mints and stashes a PKCE pair the same way sign-in does. `null` when it
 * cannot (no WebCrypto, or storage refused it): the request then carries no
 * challenge, and the callback treats the returned code like any other
 * verifier-less one.
 */
const prepareChallenge = async (): Promise<OAuthChallenge | null> => {
  if (!isWebCryptoAvailable()) {
    ddLogger.info('login pkce unavailable', {
      reason: 'webcrypto-unavailable',
      flow: 'passwordless',
    })
    return null
  }
  const challenge = await mintOAuthChallenge()
  // A fresh login gets a fresh retry budget.
  clearReauthorizeAttempted()
  if (!storeOAuthChallenge(challenge)) {
    ddLogger.warn(
      'Could not persist the OAuth challenge; the callback will find no pair and must re-authorize',
      { flow: 'passwordless' }
    )
    return null
  }
  return challenge
}

/**
 * Builds the FusionAuth passwordless completion URL for `code`.
 *
 * @param code               one-time code from set-password's `passwordlessCode`
 * @param redirectAfterLogin where the callback sends the user afterwards
 *                           (untrusted; the callback validates it as usual)
 * @param extraState         further carried `state` keys (e.g. the MAO
 *                           `subflow` / `redirectAfterSignup`)
 */
export const buildPasswordlessLoginUrl = async (
  code: string,
  redirectAfterLogin?: string,
  extraState: Record<string, unknown> = {}
): Promise<string> => {
  const challenge = await prepareChallenge()
  const params = queryString.stringify({
    client_id: clientId,
    redirect_uri: `${getOrigin()}${baseUrl}/login`,
    response_type: 'code',
    scope: 'offline_access',
    state: JSON.stringify(
      withCsrf(
        {
          role: 'buyer',
          ...extraState,
          ...(redirectAfterLogin ? { redirectAfterLogin } : {}),
        },
        challenge
      )
    ),
    ...challengeParams(challenge),
  })
  return `${oauth2.passwordless}/${encodeURIComponent(code)}?${params}`
}
